export interface User {
  _id: string;
  employeeId: string;
  isKiosk?: boolean;
  name?: string; // deprecated; display names come from contacts
  title: string; // legacy; position is handled in contacts; may be empty
  accessLevel: string;
  username: string;
  createdAt: string;
  updatedAt: string;
  /** Set by server on successful login; admin list only (omitted from /me). */
  lastLoginAt?: string;
}

export interface CreateUserData {
  employeeId?: string;
  username: string;
  accessLevel: string;
  isKiosk?: boolean;
  password: string;
}

export interface UpdateUserData {
  employeeId?: string;
  username?: string;
  accessLevel?: string;
  isKiosk?: boolean;
  password?: string;
}

export interface UpdateUserResult {
  user: User;
  accessToken?: string;
}

import { apiFetch } from '../utils/apiFetch';
import { API_BASE_URL } from '../utils/apiBaseUrl';

class UserService {
  private baseUrl = API_BASE_URL;
  
  private authHeaders(): HeadersInit | undefined {
    const token = localStorage.getItem('lts_jwt_token');
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }

  async getUsers(): Promise<User[]> {
    try {
      const response = await apiFetch(`${this.baseUrl}/users`, { headers: this.authHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getUserById(id: string): Promise<User> {
    try {
      const response = await apiFetch(`${this.baseUrl}/users/${id}`, { headers: this.authHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async createUser(userData: CreateUserData): Promise<User> {
    try {
      const response = await apiFetch(`${this.baseUrl}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authHeaders() || {}),
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(id: string, userData: UpdateUserData): Promise<UpdateUserResult> {
    try {
      const response = await apiFetch(`${this.baseUrl}/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authHeaders() || {}),
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user');
      }

      const result = await response.json();
      return {
        user: result.data,
        accessToken: typeof result.accessToken === 'string' ? result.accessToken : undefined,
      };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(id: string, currentPassword: string): Promise<void> {
    try {
      const response = await apiFetch(`${this.baseUrl}/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authHeaders() || {}),
        },
        body: JSON.stringify({ currentPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async adminRegenRecoveryCodes(userId: string): Promise<string[]> {
    const response = await apiFetch(`${this.baseUrl}/users/${userId}/mfa/regen`, {
      method: 'POST',
      headers: { ...this.authHeaders() }
    })
    const data = await response.json()
    if (!response.ok || !data.success) throw new Error(data.message || 'Failed to regenerate recovery codes')
    return data.recoveryCodes || []
  }

  async adminMfaStatus(userId: string): Promise<{ passkeyCount: number; totpEnabled: boolean; hasAnyMfa: boolean; remainingRecoveryCodes: number }> {
    const response = await apiFetch(`${this.baseUrl}/users/${userId}/mfa/status`, {
      headers: { ...this.authHeaders() }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) throw new Error(data.message || 'Failed to load MFA status');
    return {
      passkeyCount: Number(data.passkeyCount) || 0,
      totpEnabled: !!data.totpEnabled,
      hasAnyMfa: !!data.hasAnyMfa,
      remainingRecoveryCodes: Number(data.remainingRecoveryCodes) || 0
    };
  }

  async adminResetPasskeys(userId: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/users/${userId}/mfa/passkeys/reset`, {
      method: 'POST',
      headers: { ...this.authHeaders() }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) throw new Error(data.message || 'Failed to reset passkeys');
  }

  async completeRequiredPasswordChange(newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    const response = await apiFetch(`${this.baseUrl}/users/me/required-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(this.authHeaders() || {}),
      },
      body: JSON.stringify({ newPassword, confirmPassword }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Failed to change password.');
    }
    return { message: data.message || 'Password updated successfully.' };
  }

  async sendTempPassword(userId: string): Promise<{ message: string }> {
    const response = await apiFetch(`${this.baseUrl}/users/${userId}/temp-password`, {
      method: 'POST',
      headers: { ...this.authHeaders() },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Failed to email a temporary password');
    }
    return { message: data.message || 'Temporary password emailed to the address on file.' };
  }

  async adminResetTotp(userId: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/users/${userId}/mfa/totp/reset`, {
      method: 'POST',
      headers: { ...this.authHeaders() }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) throw new Error(data.message || 'Failed to reset TOTP');
  }
}

export const userService = new UserService(); 